import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Calendar, BookOpen, Users, FileText, PlayCircle, Video, ListChecks, Trash2, AlertTriangle, Edit3, Copy, Share2, X, CheckCircle2, CheckSquare, Square, Clock, FastForward, Rewind, ShieldCheck, ExternalLink } from "lucide-react";

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [quizzes, setQuizzes] = useState([]);

  const reload = () => {
    api.get("/courses").then(({ data }) => setCourses(data));
    api.get("/classes", { params: { teacher_id: user.id } }).then(({ data }) => setClasses(data));
    api.get("/assignments").then(({ data }) => setAssignments(data));
    api.get("/recordings").then(({ data }) => setRecordings(data));
    api.get("/quizzes").then(({ data }) => setQuizzes(data));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const [notesPending, setNotesPending] = useState([]);
  useEffect(() => { api.get("/teacher/notes-pending").then(({ data }) => setNotesPending(data)).catch(() => {}); }, []);
  const notifyNotes = async (classId, title) => {
    try {
      const { data } = await api.post("/teacher/notify-notes-uploaded", { class_id: classId });
      toast.success(`Notified ${data.sent}/${data.total} students about ${title}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="teacher-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-fuchsia-300">Teacher Dashboard</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">Welcome, {user.name?.split(" ")[0]}</h1>
        </div>
        <div className="flex gap-3 text-sm flex-wrap">
          <Stat icon={BookOpen} label="Courses" value={courses.length} />
          <Stat icon={Calendar} label="Live classes" value={classes.length} />
          <Stat icon={FileText} label="Assignments" value={assignments.length} />
        </div>
      </div>

      {notesPending.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-400/30 bg-amber-500/10 p-4" data-testid="notes-pending-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-amber-200">📚 {notesPending.length} class{notesPending.length > 1 ? "es" : ""} need notes/resources</div>
              <div className="text-xs text-amber-100/70 mt-0.5">Recently-ended classes have no notes uploaded. Students are waiting!</div>
              <div className="mt-3 space-y-1.5">
                {notesPending.slice(0, 5).map((c) => (
                  <div key={c.id} className="text-xs flex items-center gap-2 flex-wrap bg-black/20 rounded px-2 py-1.5" data-testid={`notes-pending-${c.id}`}>
                    <span className="font-medium">{c.title}</span>
                    <span className="text-amber-100/50">• {new Date(c.scheduled_at).toLocaleDateString()}</span>
                    <button onClick={() => notifyNotes(c.id, c.title)} className="ml-auto text-[10px] px-2 py-0.5 rounded bg-[#FF0066]/20 text-[#FF0066] hover:bg-[#FF0066]/30 font-semibold">Notify students</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="classes" className="w-full">
        <TabsList className="bg-transparent border border-white/10 rounded-full p-1 mb-6 overflow-x-auto">
          <TabsTrigger value="classes" data-testid="t-tab-classes"><Calendar className="w-4 h-4 mr-1" /> Live Classes</TabsTrigger>
          <TabsTrigger value="courses" data-testid="t-tab-courses"><BookOpen className="w-4 h-4 mr-1" /> Courses</TabsTrigger>
          <TabsTrigger value="recordings" data-testid="t-tab-recordings"><PlayCircle className="w-4 h-4 mr-1" /> Recordings</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="t-tab-assignments"><FileText className="w-4 h-4 mr-1" /> Assignments</TabsTrigger>
          <TabsTrigger value="quizzes" data-testid="t-tab-quizzes"><ListChecks className="w-4 h-4 mr-1" /> Quizzes</TabsTrigger>
        </TabsList>

        <TabsContent value="classes">
          <ClassesTabContent classes={classes} courses={courses} onChange={reload} />
        </TabsContent>

        <TabsContent value="courses">
          <div className="flex justify-between items-center mb-4">
            <div className="font-display text-lg font-semibold">Your courses</div>
            <CreateCourseDialog onCreated={reload} />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => (
              <div key={c.id} className="glass lift rounded-2xl p-4">
                <div className="text-xs uppercase tracking-wider text-fuchsia-300">{c.category}</div>
                <div className="font-display text-base font-semibold mt-1 line-clamp-2">{c.title}</div>
                <div className="text-xs text-[var(--text-mute)] mt-2 inline-flex items-center gap-1"><Users className="w-3 h-3" /> {c.enrolled_count} students</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="recordings">
          <div className="flex justify-between items-center mb-4">
            <div className="font-display text-lg font-semibold">Recordings</div>
            <UploadRecordingDialog courses={courses} onCreated={reload} />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recordings.length === 0 && <Empty msg="No recordings uploaded yet." />}
            {recordings.map((r) => (
              <div key={r.id} className="glass rounded-2xl overflow-hidden">
                <div className="aspect-video bg-[var(--bg-elev-1)] relative">
                  {r.thumbnail ? <img src={r.thumbnail} className="w-full h-full object-cover" alt="" /> : null}
                </div>
                <div className="p-3">
                  <div className="text-sm font-semibold line-clamp-1">{r.title}</div>
                  <div className="text-xs text-[var(--text-mute)]">{r.duration_minutes} min</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="assignments">
          <div className="flex justify-between items-center mb-4">
            <div className="font-display text-lg font-semibold">Assignments</div>
            <CreateAssignmentDialog courses={courses} onCreated={reload} />
          </div>
          <div className="space-y-3">
            {assignments.length === 0 && <Empty msg="No assignments created." />}
            {assignments.map((a) => (
              <div key={a.id} className="glass rounded-xl p-4">
                <div className="text-sm font-semibold">{a.title}</div>
                <div className="text-xs text-[var(--text-mute)] mt-0.5">{a.course_title}{a.due_date ? ` • Due ${new Date(a.due_date).toLocaleDateString()}` : ""}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="quizzes">
          <div className="flex justify-between items-center mb-4">
            <div className="font-display text-lg font-semibold">Quizzes</div>
            <CreateQuizDialog courses={courses} onCreated={reload} />
          </div>
          <div className="space-y-3">
            {quizzes.length === 0 && <Empty msg="No quizzes created. Click + New quiz to add one." />}
            {quizzes.map((q) => (
              <div key={q.id} className="glass rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`t-quiz-${q.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{q.title}</div>
                  <div className="text-xs text-[var(--text-mute)] mt-0.5">{q.course_title} • {q.questions.length} Qs • {q.duration_minutes} min • {q.total_marks} marks • Pass {q.pass_percentage}%</div>
                </div>
                <button onClick={async () => {
                  if (!confirm(`Delete quiz "${q.title}"?`)) return;
                  try { await api.delete(`/quizzes/${q.id}`); toast.success("Deleted"); reload(); }
                  catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                }} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300" data-testid={`delete-quiz-${q.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="glass rounded-xl px-4 py-2 inline-flex items-center gap-2">
      <Icon className="w-4 h-4 text-fuchsia-300" />
      <div>
        <div className="text-xs text-[var(--text-mute)]">{label}</div>
        <div className="font-display text-lg font-semibold leading-none">{value}</div>
      </div>
    </div>
  );
}
function Empty({ msg }) { return <div className="glass rounded-2xl p-8 text-center text-[var(--text-dim)]">{msg}</div>; }

function CreateClassDialog({ courses, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ course_id: "", title: "", description: "", scheduled_at: "", duration_minutes: 60, is_webinar: false, auto_create_zoom: true });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/classes", { ...f, scheduled_at: new Date(f.scheduled_at).toISOString() });
      toast.success(f.is_webinar ? "Webinar scheduled on Zoom!" : "Class scheduled on Zoom!");
      setOpen(false); onCreated();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-primary text-sm" data-testid="open-create-class"><Plus className="w-4 h-4" /> Schedule class</button></DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10">
        <DialogHeader><DialogTitle>Schedule live class / webinar</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <select required value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid="class-course">
            <option value="">Select course…</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <input required placeholder="Class title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid="class-title" />
          <textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          <input type="datetime-local" required value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid="class-time" />
          <input type="number" min="15" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: Number(e.target.value) })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" placeholder="Duration (min)" />
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm glass rounded-xl px-3 py-2 cursor-pointer">
              <input type="checkbox" checked={f.is_webinar} onChange={(e) => setF({ ...f, is_webinar: e.target.checked })} data-testid="class-is-webinar" />
              Live Webinar
            </label>
            <label className="flex items-center gap-2 text-sm glass rounded-xl px-3 py-2 cursor-pointer">
              <input type="checkbox" checked={f.auto_create_zoom} onChange={(e) => setF({ ...f, auto_create_zoom: e.target.checked })} />
              Create on Zoom
            </label>
          </div>
          <div className="text-xs text-[var(--text-mute)]">Zoom Pro features: HD video, screen share, cloud recording, breakout rooms, webinars. Agar Zoom config nahi hai toh Jitsi fallback automatically use hoga.</div>
          <button className="btn-primary w-full justify-center" data-testid="submit-create-class">Schedule</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateCourseDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", category: "JEE", price: 0, discount_price: 0, thumbnail: "", duration_weeks: 8, is_free: false });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/courses", f);
      toast.success("Course created"); setOpen(false); onCreated();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-primary text-sm" data-testid="open-create-course"><Plus className="w-4 h-4" /> New course</button></DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10">
        <DialogHeader><DialogTitle>Create course</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid="course-title" />
          <textarea required placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm">
            {["Full Course","Bootcamp","SEO","Graphic Design","Meta Ads","Google Ads","WordPress","Shopify","Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input type="number" placeholder="Price ₹" value={f.price} onChange={(e) => setF({ ...f, price: Number(e.target.value) })} className="bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
            <input type="number" placeholder="Discount ₹" value={f.discount_price} onChange={(e) => setF({ ...f, discount_price: Number(e.target.value) })} className="bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          </div>
          <input placeholder="Thumbnail URL" value={f.thumbnail} onChange={(e) => setF({ ...f, thumbnail: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_free} onChange={(e) => setF({ ...f, is_free: e.target.checked })} /> Free course</label>
          <button className="btn-primary w-full justify-center">Create</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UploadRecordingDialog({ courses, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ course_id: "", title: "", video_url: "", duration_minutes: 60, thumbnail: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/recordings", f); toast.success("Recording added"); setOpen(false); onCreated(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-primary text-sm"><Plus className="w-4 h-4" /> Add recording</button></DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10">
        <DialogHeader><DialogTitle>Upload recording</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <select required value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm">
            <option value="">Select course…</option>{courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <input required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          <input required placeholder="Video URL (YouTube / Bunny / R2 link)" value={f.video_url} onChange={(e) => setF({ ...f, video_url: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          <input placeholder="Thumbnail URL" value={f.thumbnail} onChange={(e) => setF({ ...f, thumbnail: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          <input type="number" placeholder="Duration (min)" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: Number(e.target.value) })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          <button className="btn-primary w-full justify-center">Add</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateAssignmentDialog({ courses, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ course_id: "", title: "", description: "", due_date: "", max_marks: 100 });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        course_id: f.course_id,
        title: f.title,
        description: f.description,
        max_marks: f.max_marks,
        due_date: f.due_date ? new Date(f.due_date).toISOString() : null,
      };
      await api.post("/assignments", body); toast.success("Assignment created"); setOpen(false); onCreated();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-primary text-sm" data-testid="open-create-assignment"><Plus className="w-4 h-4" /> New assignment</button></DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10">
        <DialogHeader><DialogTitle>Create assignment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="asgn-create-form">
          <select required value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm">
            <option value="">Select course…</option>{courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <input required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          <textarea required placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          <input type="datetime-local" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" placeholder="Due date (optional)" />
          <input type="number" value={f.max_marks} onChange={(e) => setF({ ...f, max_marks: Number(e.target.value) })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" placeholder="Max marks" />
          <button className="btn-primary w-full justify-center">Create</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateQuizDialog({ courses, onCreated }) {
  const [open, setOpen] = useState(false);
  const empty = { course_id: "", title: "", description: "", duration_minutes: 15, pass_percentage: 60, questions: [emptyQ()] };
  const [f, setF] = useState(empty);

  function emptyQ() { return { text: "", options: ["", "", "", ""], correct_index: 0, marks: 1, explanation: "" }; }

  const updateQ = (i, patch) => {
    const qs = [...f.questions]; qs[i] = { ...qs[i], ...patch }; setF({ ...f, questions: qs });
  };
  const updateOpt = (i, oi, val) => {
    const qs = [...f.questions]; const opts = [...qs[i].options]; opts[oi] = val; qs[i] = { ...qs[i], options: opts }; setF({ ...f, questions: qs });
  };
  const addQ = () => setF({ ...f, questions: [...f.questions, emptyQ()] });
  const delQ = (i) => setF({ ...f, questions: f.questions.filter((_, j) => j !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (f.questions.some((q) => !q.text.trim() || q.options.some((o) => !o.trim()))) {
      toast.error("All questions and options must be filled");
      return;
    }
    try {
      await api.post("/quizzes", f);
      toast.success("Quiz created");
      setOpen(false); setF(empty); onCreated();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-primary text-sm" data-testid="open-create-quiz"><Plus className="w-4 h-4" /> New quiz</button></DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create quiz</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="quiz-create-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <select required value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid="quiz-course">
              <option value="">Select course…</option>{courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <input required placeholder="Quiz title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid="quiz-title" />
            <input type="number" min="1" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: Number(e.target.value) })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" placeholder="Duration (min)" />
            <input type="number" min="1" max="100" value={f.pass_percentage} onChange={(e) => setF({ ...f, pass_percentage: Number(e.target.value) })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" placeholder="Pass %" />
          </div>
          <textarea placeholder="Description (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" />

          <div className="text-xs uppercase tracking-wider text-fuchsia-300 pt-2">Questions ({f.questions.length})</div>
          {f.questions.map((q, i) => (
            <div key={i} className="glass rounded-xl p-3 space-y-2" data-testid={`qz-q-${i}`}>
              <div className="flex items-center gap-2">
                <div className="text-xs font-bold text-fuchsia-300">Q{i + 1}</div>
                {f.questions.length > 1 && (
                  <button type="button" onClick={() => delQ(i)} className="ml-auto p-1 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
              <input required placeholder="Question text" value={q.text} onChange={(e) => updateQ(i, { text: e.target.value })} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <div className="grid sm:grid-cols-2 gap-2">
                {q.options.map((opt, oi) => (
                  <label key={oi} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm border cursor-pointer ${q.correct_index === oi ? "border-fuchsia-400/50 bg-fuchsia-500/10" : "border-white/10"}`}>
                    <input type="radio" name={`q${i}`} checked={q.correct_index === oi} onChange={() => updateQ(i, { correct_index: oi })} className="accent-fuchsia-500" />
                    <input required placeholder={`Option ${oi + 1}`} value={opt} onChange={(e) => updateOpt(i, oi, e.target.value)} className="flex-1 bg-transparent text-sm focus:outline-none" />
                  </label>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <input type="number" min="1" value={q.marks} onChange={(e) => updateQ(i, { marks: Number(e.target.value) })} placeholder="Marks" className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-lg px-3 py-1.5 text-xs" />
                <input value={q.explanation} onChange={(e) => updateQ(i, { explanation: e.target.value })} placeholder="Explanation (optional)" className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-lg px-3 py-1.5 text-xs" />
              </div>
            </div>
          ))}
          <button type="button" onClick={addQ} className="btn-ghost w-full justify-center text-xs" data-testid="add-question"><Plus className="w-4 h-4" /> Add question</button>
          <button className="btn-primary w-full justify-center" data-testid="quiz-create-submit"><ListChecks className="w-4 h-4" /> Create quiz</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ====================================================== CLASSES TAB CONTAINER
function ClassesTabContent({ classes, courses, onChange }) {
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAll = () => setSelected(new Set(classes.map((c) => c.id)));
  const clearSel = () => setSelected(new Set());

  const bulkCancel = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Cancel ${selected.size} selected classes?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/classes/bulk-cancel", { class_ids: [...selected] });
      toast.success(`Cancelled ${data.cancelled}/${data.requested}`);
      clearSel(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk cancel failed"); }
    finally { setBusy(false); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Permanently DELETE ${selected.size} classes? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/classes/bulk-delete", { class_ids: [...selected] });
      toast.success(`Deleted ${data.deleted}/${data.requested}`);
      clearSel(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
    finally { setBusy(false); }
  };

  const bulkReschedule = async (offsetMin, label) => {
    if (selected.size === 0) return;
    if (!confirm(`Shift ${selected.size} classes by ${label}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/classes/bulk-reschedule", { class_ids: [...selected], minutes_offset: offsetMin });
      toast.success(`Rescheduled ${data.rescheduled} classes by ${label}`);
      clearSel(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Reschedule failed"); }
    finally { setBusy(false); }
  };

  const allSelected = classes.length > 0 && selected.size === classes.length;

  return (
    <>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div className="font-display text-lg font-semibold">Your live sessions ({classes.length})</div>
        <div className="flex items-center gap-2 flex-wrap">
          {classes.length > 0 && (
            <button onClick={allSelected ? clearSel : selectAll}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center gap-1"
                    data-testid="select-all-classes">
              {allSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
              {allSelected ? "Deselect all" : "Select all"}
            </button>
          )}
          <CreateClassDialog courses={courses} onCreated={onChange} />
        </div>
      </div>

      {/* Bulk action toolbar */}
      {selected.size > 0 && (
        <div className="glass-strong rounded-xl p-3 mb-4 flex items-center justify-between gap-3 flex-wrap border border-fuchsia-500/30" data-testid="bulk-toolbar">
          <div className="text-sm font-semibold text-fuchsia-300">
            {selected.size} selected · Bulk actions:
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => bulkReschedule(60, "+1 hour")} disabled={busy}
                    className="text-xs px-2.5 py-1.5 rounded-md bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 inline-flex items-center gap-1"
                    data-testid="bulk-push-1h">
              <FastForward className="w-3.5 h-3.5" /> +1 h
            </button>
            <button onClick={() => bulkReschedule(60 * 24, "+1 day")} disabled={busy}
                    className="text-xs px-2.5 py-1.5 rounded-md bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 inline-flex items-center gap-1"
                    data-testid="bulk-push-1d">
              <FastForward className="w-3.5 h-3.5" /> +1 day
            </button>
            <button onClick={() => bulkReschedule(60 * 24 * 7, "+1 week")} disabled={busy}
                    className="text-xs px-2.5 py-1.5 rounded-md bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 inline-flex items-center gap-1"
                    data-testid="bulk-push-1w">
              <FastForward className="w-3.5 h-3.5" /> +1 week
            </button>
            <button onClick={() => bulkReschedule(-60 * 24, "-1 day")} disabled={busy}
                    className="text-xs px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 inline-flex items-center gap-1"
                    data-testid="bulk-pull-1d">
              <Rewind className="w-3.5 h-3.5" /> -1 day
            </button>
            <button onClick={bulkCancel} disabled={busy}
                    className="text-xs px-2.5 py-1.5 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 inline-flex items-center gap-1"
                    data-testid="bulk-cancel">
              <AlertTriangle className="w-3.5 h-3.5" /> Cancel all
            </button>
            <button onClick={bulkDelete} disabled={busy}
                    className="text-xs px-2.5 py-1.5 rounded-md bg-red-500/15 hover:bg-red-500/25 text-red-300 inline-flex items-center gap-1"
                    data-testid="bulk-delete">
              <Trash2 className="w-3.5 h-3.5" /> Delete all
            </button>
            <button onClick={clearSel}
                    className="text-xs px-2.5 py-1.5 rounded-md hover:bg-white/5 inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {classes.length === 0 && <Empty msg="No classes yet. Schedule one." />}
        {classes.map((c) => (
          <ClassCard key={c.id}
                     cls={c}
                     selected={selected.has(c.id)}
                     onToggleSelect={() => toggle(c.id)}
                     onChange={onChange} />
        ))}
      </div>
    </>
  );
}

// ====================================================== CLASS MANAGEMENT CARD
function ClassCard({ cls, onChange, selected, onToggleSelect }) {
  const [editing, setEditing] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: cls.title || "",
    description: cls.description || "",
    scheduled_at: cls.scheduled_at ? toLocalInput(cls.scheduled_at) : "",
    duration_minutes: cls.duration_minutes || 60,
  });
  const status = (cls.status || "scheduled").toLowerCase();

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      await api.patch(`/classes/${cls.id}`, {
        title: form.title.trim(),
        description: form.description,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || 60,
      });
      toast.success("Class updated");
      setEditing(false);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const setStatus = async (newStatus) => {
    if (!confirm(`Mark this class as "${newStatus}"?`)) return;
    setBusy(true);
    try {
      await api.patch(`/classes/${cls.id}/status`, { status_val: newStatus });
      toast.success(`Marked ${newStatus}`);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm(`Delete "${cls.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/classes/${cls.id}`);
      toast.success("Class deleted");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const quickReschedule = async (minutes, label) => {
    if (!confirm(`Reschedule "${cls.title}" by ${label}?`)) return;
    setBusy(true);
    try {
      const oldDate = new Date(cls.scheduled_at);
      const newDate = new Date(oldDate.getTime() + minutes * 60000);
      await api.patch(`/classes/${cls.id}`, { scheduled_at: newDate.toISOString() });
      toast.success(`Rescheduled by ${label}`);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reschedule failed");
    } finally { setBusy(false); }
  };

  const shareUrl = `${window.location.origin}/live/${cls.id}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const shareWA = () => {
    const msg = encodeURIComponent(`📚 *${cls.title}*\n${cls.course_title}\n🕐 ${new Date(cls.scheduled_at).toLocaleString()}\n\nJoin: ${shareUrl}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  if (editing) {
    return (
      <div className="glass rounded-xl p-4 space-y-3 border border-fuchsia-500/30" data-testid={`edit-class-${cls.id}`}>
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-semibold">Edit class</div>
          <button onClick={() => setEditing(false)} className="p-1 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <label className="block sm:col-span-2">
            <div className="text-[10px] text-[var(--text-mute)] mb-1">Class name / title</div>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                   className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2"
                   data-testid={`edit-title-${cls.id}`} />
          </label>
          <label className="block">
            <div className="text-[10px] text-[var(--text-mute)] mb-1">Scheduled date / time</div>
            <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
                   className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2"
                   data-testid={`edit-time-${cls.id}`} />
          </label>
          <label className="block">
            <div className="text-[10px] text-[var(--text-mute)] mb-1">Duration (minutes)</div>
            <input type="number" min="15" max="240" value={form.duration_minutes} onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))}
                   className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2"
                   data-testid={`edit-duration-${cls.id}`} />
          </label>
          <label className="block sm:col-span-2">
            <div className="text-[10px] text-[var(--text-mute)] mb-1">Description (optional)</div>
            <textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2"
                      data-testid={`edit-desc-${cls.id}`} />
          </label>
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className="btn-primary !py-1.5 !px-3 text-xs disabled:opacity-50" data-testid={`save-class-${cls.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save changes"}
          </button>
          <button onClick={() => setEditing(false)} className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`glass rounded-xl p-4 ${selected ? "ring-2 ring-fuchsia-500/50 bg-fuchsia-500/[0.04]" : ""}`} data-testid={`class-card-${cls.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {onToggleSelect && (
            <button onClick={onToggleSelect}
                    className={`mt-0.5 shrink-0 ${selected ? "text-fuchsia-300" : "text-[var(--text-mute)] hover:text-white"}`}
                    title={selected ? "Deselect" : "Select"}
                    data-testid={`select-class-${cls.id}`}>
              {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            </button>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-semibold truncate">{cls.title}</div>
              <StatusBadge status={status} />
            </div>
            <div className="text-xs text-[var(--text-mute)] mt-0.5">
              {cls.course_title} · {new Date(cls.scheduled_at).toLocaleString()} · {cls.duration_minutes || 60} min
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {status !== "cancelled" && status !== "ended" && (
            <button onClick={() => setShowChecklist(true)}
                    className="text-xs px-3 py-1.5 rounded-md bg-gradient-to-r from-fuchsia-500/20 to-pink-500/20 hover:from-fuchsia-500/30 hover:to-pink-500/30 text-pink-300 inline-flex items-center gap-1 font-semibold"
                    data-testid={`start-class-${cls.id}`}>
              <Video className="w-3.5 h-3.5" /> Start
            </button>
          )}
          <QuickRescheduleButton onPick={quickReschedule} busy={busy} testId={cls.id} />
          <button onClick={() => setEditing(true)} title="Edit"
                  className="text-xs px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 inline-flex items-center gap-1"
                  data-testid={`edit-class-btn-${cls.id}`}>
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={copyLink} title="Copy share link"
                  className="text-xs px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 inline-flex items-center gap-1"
                  data-testid={`copy-class-${cls.id}`}>
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button onClick={shareWA} title="Share via WhatsApp"
                  className="text-xs px-2.5 py-1.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 inline-flex items-center gap-1"
                  data-testid={`share-class-${cls.id}`}>
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
          {status === "scheduled" && (
            <button onClick={() => setStatus("cancelled")} disabled={busy} title="Mark cancelled"
                    className="text-xs px-2.5 py-1.5 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 inline-flex items-center gap-1"
                    data-testid={`cancel-class-${cls.id}`}>
              <AlertTriangle className="w-3.5 h-3.5" /> Cancel
            </button>
          )}
          {status === "live" && (
            <button onClick={() => setStatus("ended")} disabled={busy} title="Mark class ended"
                    className="text-xs px-2.5 py-1.5 rounded-md bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 inline-flex items-center gap-1"
                    data-testid={`end-class-${cls.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark ended
            </button>
          )}
          <button onClick={remove} disabled={busy} title="Delete class"
                  className="text-xs px-2.5 py-1.5 rounded-md bg-red-500/15 hover:bg-red-500/25 text-red-300 inline-flex items-center gap-1"
                  data-testid={`delete-class-${cls.id}`}>
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>
      {showChecklist && (
        <PreClassChecklist cls={cls} onClose={() => setShowChecklist(false)} />
      )}
    </div>
  );
}

// ====================================================== QUICK-RESCHEDULE POPOVER
function QuickRescheduleButton({ onPick, busy, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} disabled={busy} title="Quick reschedule"
              className="text-xs px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 inline-flex items-center gap-1"
              data-testid={`quick-reschedule-btn-${testId}`}>
        <Clock className="w-3.5 h-3.5" /> Move
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-40 glass-strong rounded-lg p-2 min-w-[140px] flex flex-col gap-1 text-xs shadow-2xl">
            {[
              [15, "+15 min"],
              [60, "+1 hour"],
              [60 * 24, "+1 day"],
              [60 * 24 * 7, "+1 week"],
              [-60, "-1 hour"],
              [-60 * 24, "-1 day"],
            ].map(([min, label]) => (
              <button key={min}
                      onClick={() => { setOpen(false); onPick(min, label); }}
                      className="text-left px-2 py-1.5 rounded hover:bg-fuchsia-500/15 hover:text-fuchsia-300 transition"
                      data-testid={`quick-reschedule-${testId}-${min}`}>
                {min > 0 ? <FastForward className="w-3 h-3 inline mr-1.5" /> : <Rewind className="w-3 h-3 inline mr-1.5" />}
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ====================================================== PRE-CLASS CHECKLIST
function PreClassChecklist({ cls, onClose }) {
  const [notes, setNotes] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const minutesUntil = Math.round((new Date(cls.scheduled_at).getTime() - Date.now()) / 60000);
  const isLive = (cls.status || "").toLowerCase() === "live";
  const startedRecently = minutesUntil > -60 && minutesUntil <= 0;
  const canJoin = isLive || (minutesUntil >= -10 && minutesUntil <= 30);

  useEffect(() => {
    api.get("/classes").then(({ data }) => {
      // Pull notes + recordings for THIS class
      api.get(`/classes/${cls.id}/notes`).then(({ data: n }) => setNotes(n || [])).catch(() => setNotes([]));
      api.get("/recordings").then(({ data: r }) => {
        setRecordings((r || []).filter((rec) => rec.class_id === cls.id));
      }).catch(() => setRecordings([]));
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const hasZoom = !!cls.zoom_join_url || !!cls.meeting_link;
  const hasRecording = recordings.length > 0;
  const hasNotes = notes.length > 0;
  const items = [
    { key: "meeting", label: "Meeting link configured", ok: hasZoom,
      hint: hasZoom ? (cls.zoom_join_url ? "Zoom" : "Custom") : "No meeting URL — students won't be able to join" },
    { key: "time", label: "Scheduled time is correct", ok: minutesUntil > -120,
      hint: minutesUntil > 0
        ? `Class starts in ${minutesUntil >= 60 ? Math.round(minutesUntil/60)+'h' : minutesUntil+'m'}`
        : startedRecently ? `Started ${-minutesUntil}m ago — join NOW` : `Class was scheduled ${-Math.round(minutesUntil/60)}h ago` },
    { key: "notes", label: "Notes / PDFs uploaded", ok: hasNotes,
      hint: hasNotes ? `${notes.length} note(s) attached` : "No PDFs yet — students will ask for materials" },
    { key: "recording", label: "Recording enabled / already exists", ok: hasZoom,
      hint: hasRecording ? `${recordings.length} recording(s) available` : "Make sure 'Record to cloud' is ON in Zoom" },
  ];
  const allReady = items.every((i) => i.ok);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-strong rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid={`pre-class-checklist-${cls.id}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-fuchsia-300" />
            <div className="font-display text-base font-semibold">Pre-class checklist</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-xs text-[var(--text-mute)] mb-3 truncate">{cls.title} · {new Date(cls.scheduled_at).toLocaleString()}</div>
        <div className="space-y-2 mb-4">
          {items.map((it) => (
            <div key={it.key} className="flex items-start gap-2 text-sm" data-testid={`checklist-${it.key}`}>
              {it.ok
                ? <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
                : <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />}
              <div className="flex-1">
                <div className={it.ok ? "text-white" : "text-amber-200"}>{it.label}</div>
                <div className="text-[11px] text-[var(--text-mute)]">{it.hint}</div>
              </div>
            </div>
          ))}
        </div>

        {!allReady && (
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 text-xs text-amber-200 mb-4">
            ⚠️ Kuch items missing hain. Aap fir bhi class start kar sakte ho but students ko issue ho sakta hai.
          </div>
        )}
        {!canJoin && minutesUntil > 30 && (
          <div className="rounded-lg bg-cyan-500/10 border border-cyan-500/30 p-2.5 text-xs text-cyan-200 mb-4">
            ⏰ Class starts in {minutesUntil >= 60 ? Math.round(minutesUntil/60)+' hours' : minutesUntil+' minutes'}. Aap abhi bhi join kar sakte ho early-setup ke liye.
          </div>
        )}

        <div className="flex items-center gap-2">
          <Link to={`/live/${cls.id}`}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full font-semibold text-sm ${allReady ? "bg-pink-500 hover:bg-pink-600 text-white" : "bg-amber-500/30 hover:bg-amber-500/40 text-amber-100"}`}
                data-testid={`pre-class-start-${cls.id}`}>
            <Video className="w-4 h-4" /> {allReady ? "Start class" : "Start anyway"}
          </Link>
          {cls.zoom_join_url && (
            <a href={cls.zoom_join_url} target="_blank" rel="noreferrer"
               className="px-3 py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-xs inline-flex items-center gap-1"
               title="Open Zoom directly">
              <ExternalLink className="w-3.5 h-3.5" /> Zoom
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const conf = {
    scheduled: { bg: "bg-fuchsia-500/15", color: "text-fuchsia-300", label: "Scheduled" },
    live:      { bg: "bg-emerald-500/15", color: "text-emerald-300 animate-pulse", label: "● Live" },
    ended:     { bg: "bg-white/5",         color: "text-[var(--text-mute)]", label: "Ended" },
    cancelled: { bg: "bg-amber-500/15",   color: "text-amber-300", label: "Cancelled" },
  }[status] || { bg: "bg-white/5", color: "text-[var(--text-mute)]", label: status };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${conf.bg} ${conf.color}`}>{conf.label}</span>;
}

// ISO datetime → datetime-local input format (no Z, trimmed seconds)
function toLocalInput(iso) {
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  } catch { return ""; }
}

